// MessageHelmet.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { withTranslation } from 'react-i18next';

import Redirect from '../container/Redirect.js';
import { isClient } from '../resource/getUserAgent.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export class MessageHelmet extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      messageId,
      senderId,
      senderUsername,
      assetIds,
      fetchMessageDetailError,
      isMessageDetailFetched,
      isMessageDetailFetching,
      isUserDetailFetched,
      isUserDetailFetching,
      staticContext,
      fetchMessageDetail,
      fetchUser,
    } = props;
    if (staticContext.actions) {
      if (fetchMessageDetailError) {
        staticContext.error = fetchMessageDetailError;
      }
      const shouldFetchMessageDetail =
        messageId &&
        (!senderId || assetIds.length === 0) &&
        !isMessageDetailFetched &&
        !isMessageDetailFetching;
      const shouldFetchUser =
        senderId &&
        !senderUsername &&
        !isUserDetailFetched &&
        !isUserDetailFetching;
      if (shouldFetchMessageDetail) {
        staticContext.actions.push({
          function: fetchMessageDetail,
          argumentObject: {
            id: messageId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (shouldFetchUser) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            id: senderId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }
  render() {
    const {
      titleI18nKey,
      descriptionI18nKey,
      keywordsI18nKey,
      messageId,
      senderId,
      senderUsername,
      ogMessageThumbnail,
      title,
      captionText,
      firstAssetContentType,
      pathnamePrefix,
      staticContext,
      t,
      fetchMessageDetailError,
    } = this.props;
    const ssrHostname = staticContext?.requestHeaders?.hostname || 'swag.live';
    const hostname = isClient ? window.location.hostname : ssrHostname;
    const translationParameters = {
      cp_name: senderUsername,
      message_title: title,
      post_title: title,
      message_description: captionText,
      post_description: captionText,
      ns: TranslationNamespace.SEO,
    };
    const displayTitle = t(titleI18nKey, translationParameters);
    const displayDescription = t(descriptionI18nKey, translationParameters);
    const displayKeywords = t(keywordsI18nKey, translationParameters);
    const isVideo = firstAssetContentType.startsWith('video/');

    if (fetchMessageDetailError?.message) {
      const status = JSON.parse(fetchMessageDetailError.message)?.status;
      return (
        <Redirect
          to={status === 404 || status === 403 ? '/404' : '/error'}
          status={status}
          replace={true}
        />
      );
    }

    return (
      <>
        <Helmet>
          <title>{displayTitle}</title>
          <meta name="description" content={displayDescription} />
          <meta name="keywords" content={displayKeywords} />
          <meta
            property="og:url"
            content={`https://${hostname}/${pathnamePrefix}/${messageId}`}
          />
          <meta property="og:title" content={displayTitle} />
          <meta property="og:description" content={displayDescription} />
          <meta property="og:image" content={ogMessageThumbnail} />
          <meta property="og:image:width" content="512" />
          <meta property="og:image:height" content="512" />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:image" content={ogMessageThumbnail} />
        </Helmet>
        {isVideo && (
          <Helmet>
            <meta property="og:type" content="video.other" />
            <meta
              property="og:video"
              content={`https://${hostname}/${pathnamePrefix}/${messageId}`}
            />
            <meta property="og:video:actor:id" content={senderId} />
            <meta property="og:video:actor:username" content={senderUsername} />
            <meta
              property="og:video:actor:url"
              content={`https://${hostname}/user/${senderId}`}
            />
          </Helmet>
        )}
      </>
    );
  }
}

MessageHelmet.propTypes = {
  titleI18nKey: PropTypes.string,
  descriptionI18nKey: PropTypes.string,
  keywordsI18nKey: PropTypes.string,
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  ogMessageThumbnail: PropTypes.string,
  title: PropTypes.string,
  captionText: PropTypes.string,
  firstAssetContentType: PropTypes.string,
  pathnamePrefix: PropTypes.string,
  isMessageDetailFetched: PropTypes.bool,
  isMessageDetailFetching: PropTypes.bool,
  isUserDetailFetched: PropTypes.bool,
  isUserDetailFetching: PropTypes.bool,
  assetIds: PropTypes.array,
  staticContext: PropTypes.object,
  fetchMessageDetailError: PropTypes.object,
  t: PropTypes.func.isRequired,
  fetchMessageDetail: PropTypes.func,
  fetchUser: PropTypes.func,
};

MessageHelmet.defaultProps = {
  titleI18nKey: 'seo_cp_post_title',
  descriptionI18nKey: 'seo_cp_post_description',
  keywordsI18nKey: 'seo_cp_post_keywords',
  messageId: '',
  senderId: '',
  senderUsername: '',
  ogMessageThumbnail: '',
  title: '',
  captionText: '',
  firstAssetContentType: '',
  pathnamePrefix: 'post',
  isMessageDetailFetched: false,
  isMessageDetailFetching: false,
  isUserDetailFetched: false,
  isUserDetailFetching: false,
  assetIds: [],
  staticContext: {},
  fetchMessageDetailError: null,
  fetchMessageDetail: () => null,
  fetchUser: () => null,
};

export default withTranslation()(withFetchTranslation(MessageHelmet));
